import React, { useMemo } from 'react';
import capitalize from 'lodash/capitalize';
import { Grid, Stack } from '@mui/material';

import { Divider, InfoField, Typography } from '@occc-app/design-system';
import {
  MembershipExternalIdentityVerificationProfileFragment,
  FuelAndConvenienceProfileFragment,
} from '@occc/graphql';

import { useConfig } from '@occc/config';
import { EaivAttemptDescription } from './EaivAttemptDescription';

import { buildFields } from './buildFields';
import { IEaivMembershipStatistics } from '../../../../../dataAccessLayer/eaiv/types';

type FuelAndConvenienceProfileProps = {
  profile?: FuelAndConvenienceProfileFragment;
  eaivStatistics: IEaivMembershipStatistics;
  externalIdentityVerificationProfiles: MembershipExternalIdentityVerificationProfileFragment[];
};

const FuelAndConvenienceProfile = (props: FuelAndConvenienceProfileProps) => {
  const { config } = useConfig();

  const fields = useMemo(() => buildFields(config.ach.enabled), [config.ach.enabled]);

  const { profile, eaivStatistics, externalIdentityVerificationProfiles } = props;

  const verificationStatus =
    externalIdentityVerificationProfiles?.flatMap(
      (profile) => profile.attributes?.map((attr) => attr.name) || [],
    ) ?? [];

  const communityVerificationStatus =
    verificationStatus.length > 0 ? verificationStatus.join(', ') : 'None';

  return (
    <>
      <Typography variant='h6' component='h3' paragraph>
        Fuel & Convenience Profile
      </Typography>
      <Grid container spacing={3}>
        {fields.map((field) => {
          let value = profile?.[field.attribute]?.toString() ?? field.defaultValue;
          value = field.valueMap?.[value] ?? capitalize(value);

          return (
            <Grid item xs={12} md={3} key={field.attribute}>
              <InfoField label={field.name} value={value} />
            </Grid>
          );
        })}
      </Grid>

      {config.externalIdentityVerification?.enabled && (
        <Grid item xs={12} md={3}>
          <InfoField label='Community Verification Status' value={communityVerificationStatus} />
        </Grid>
      )}

      <Divider sx={{ mb: 2 }} />

      <Typography variant='subtitle1' component='h4' paragraph>
        EAIV attempts
      </Typography>
      <Stack spacing={1}>
        <EaivAttemptDescription
          label='Age Submit'
          success={eaivStatistics.verificationAttempts.ageSubmitSuccess}
          failed={eaivStatistics.verificationAttempts.ageSubmitFailed}
          reasons={eaivStatistics.verificationAttempts.ageSubmitErrorReasons}
        />
        <EaivAttemptDescription
          label='Identity Start'
          success={eaivStatistics.verificationAttempts.identityStartSuccess}
          failed={eaivStatistics.verificationAttempts.identityStartFailed}
          reasons={eaivStatistics.verificationAttempts.identityStartErrorReasons}
        />
        <EaivAttemptDescription
          label='Identity Submit'
          success={eaivStatistics.verificationAttempts.identitySubmitSuccess}
          failed={eaivStatistics.verificationAttempts.identitySubmitFailed}
          reasons={eaivStatistics.verificationAttempts.identitySubmitErrorReasons}
        />
      </Stack>
    </>
  );
};

export default FuelAndConvenienceProfile;
